// Internal modules
import { getParams } from '../params';
import { logger } from '@/lib/logger';
import { REDIRECT_URLS_MAP } from './constants';
import type { GenericObjectType } from '@/types';
import { getGlobals, isBillingTypeSubscriptionV1 } from '@/lib/globals';

/**
 * @function getRedirectUrl
 * @param {string} app
 * @returns {string}
 */
const getRedirectUrl = (
  app: keyof typeof REDIRECT_URLS_MAP = 'xoon',
  additionalParams?: GenericObjectType,
): string => {
  let redirectUrl = REDIRECT_URLS_MAP[app];
  const isSubscriptionV1 = isBillingTypeSubscriptionV1();

  // TODO: Do we even need this? Since the default value is xoon,
  // this block of code won't get hit anymore.
  if (!redirectUrl) {
    // @ts-expect-error TODO this worked pre typescript
    logger.warn('redirect_url_not_found', app);
    redirectUrl = REDIRECT_URLS_MAP.xoon;
  }

  if (app === 'billing' && isSubscriptionV1) {
    redirectUrl = `${redirectUrl}/subscriptions`;
  }

  return buildUrl(redirectUrl, {
    additionalParams,
    withCurrentParams: false,
  });
};

/**
 * @function buildUrl
 * @param {string} endpoint
 * @param {object} options | additionalParams, withCurrentParams
 * @returns {string}
 */
const buildUrl = (
  endpoint?: string | null,
  options: GenericObjectType = {},
): string => {
  const { additionalParams = {}, withCurrentParams = true } = options;
  const baseParams = withCurrentParams ? getParams() : {};
  const params: GenericObjectType<string> = Object.assign(
    {},
    baseParams,
    additionalParams,
  );

  // `cmd` is a reserved slingshot param that we need to
  // remove for client redirects.
  delete params.cmd;

  // `onboardingRedirectFromHermes` is added when landing-router redirects
  // to guest/signup and we log it to FPTI when our app loads. We don't
  // want to propagate that param to other apps.
  delete params.onboardingRedirectFromHermes;

  const separator = endpoint?.includes('?') ? '&' : '?';
  const url = Object.entries(params).reduce((url, [key, value], i) => {
    const encodedKey = encodeURIComponent(key);
    const encodedValue = encodeURIComponent(value);

    return `${url}${encodedKey}=${encodedValue}&`;
  }, `${endpoint}${separator}`);

  return url.replace(/[?&]$/, '');
};

/**
 * @function removeParamsFromUrl
 * @param {string} url - The URL to remove params from
 * @param {string[]} params - The param keys to remove
 * @returns {string} - The URL with the specified params removed
 */
const removeParamsFromUrl = (url: string, params: string[]): string => {
  if (!url || params.length === 0) {
    return url;
  }

  const [base, search = ''] = url.split('?');
  const searchParams = new URLSearchParams(search);

  for (const param of params) {
    searchParams.delete(param);
  }

  const newSearch = searchParams.toString();
  return newSearch ? `${base}?${newSearch}` : base;
};

/**
 * Removes the origin from the IWC redirect URL.
 * @param redirectUrl Url to build from
 * @returns Url without the origin
 */
const buildIWCRedirectUrl = (redirectUrl?: string): string => {
  let url;

  if (redirectUrl) {
    const { uri } = getGlobals();
    // Need to pass in base since the redirectUrl we get from Severus is a
    // relative path.
    url = new URL(redirectUrl, uri.base || '');
  } else {
    const dumbledoreUrl = buildUrl(getRedirectUrl('dumbledore'), {
      withCurrentParams: true,
    });
    url = new URL(dumbledoreUrl);
  }

  return encodeURIComponent(`${url.pathname}${url.search}`);
};

export {
  buildIWCRedirectUrl,
  buildUrl,
  getParams,
  getRedirectUrl,
  removeParamsFromUrl,
};
